import { LegalPageLayout } from '../components/LegalPageLayout'

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy">
      <h1>Privacy Policy</h1>
      <p className="legal-last-updated">Last updated: May 25, 2026</p>

      <h2>1. Data Controller</h2>
      <p>
        Kwame Ofori
        <br />
        Beckstraße 20
        <br />
        20357 Hamburg
        <br />
        Germany
      </p>
      <p>
        Email: <a href="mailto:contact@beahead.ai">contact@beahead.ai</a>
      </p>
      <div className="legal-note">
        The responsible party within the meaning of Art.&nbsp;4(7) GDPR is the
        natural person named above.
      </div>

      <h2>2. What We Collect and Why</h2>
      <p>
        gymAhead is a workout planning and tracking app. We only collect data
        that is necessary to provide the service or to keep it stable and
        improve it. Below is an exhaustive list.
      </p>
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Purpose</th>
            <th>Legal Basis (GDPR)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Email address, display name, auth provider ID (when you sign in
              via email, Google, or Apple)
            </td>
            <td>Account creation and authentication</td>
            <td>Art.&nbsp;6(1)(b) — contract performance</td>
          </tr>
          <tr>
            <td>Device identifier (a random UUID generated on your device)</td>
            <td>
              Identify guest users so workouts and preferences persist without
              sign-in
            </td>
            <td>Art.&nbsp;6(1)(b) — contract performance</td>
          </tr>
          <tr>
            <td>
              Workout and session data (exercises, sets, reps, duration,
              equipment preferences)
            </td>
            <td>
              Generate personalized workouts, track your training, and save
              your preferences
            </td>
            <td>Art.&nbsp;6(1)(b) — contract performance</td>
          </tr>
          <tr>
            <td>
              App usage events (e.g.&nbsp;screens viewed, workouts generated,
              sessions completed)
            </td>
            <td>Understand how the app is used so we can improve it</td>
            <td>Art.&nbsp;6(1)(f) — legitimate interest</td>
          </tr>
          <tr>
            <td>
              Crash reports and error logs (stack traces, device model, OS
              version)
            </td>
            <td>Diagnose and fix bugs</td>
            <td>Art.&nbsp;6(1)(f) — legitimate interest</td>
          </tr>
          <tr>
            <td>Push notification token</td>
            <td>Deliver push notifications you have opted into</td>
            <td>Art.&nbsp;6(1)(a) — consent</td>
          </tr>
          <tr>
            <td>Feedback text (if you submit feedback via the in-app form)</td>
            <td>Read and act on your feedback</td>
            <td>Art.&nbsp;6(1)(f) — legitimate interest</td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>Local-only data.</strong> Workouts, session history, and your
        guest profile are also stored on your device (using Hive local
        storage). This data never leaves your device unless you are signed in
        and the app syncs it with our server.
      </p>

      <h2>3. Third-Party Processors</h2>
      <p>
        We use the following third-party services to operate gymAhead. Each
        acts as a data processor under Art.&nbsp;28 GDPR.
      </p>
      <table>
        <thead>
          <tr>
            <th>Service</th>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Data Location</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Firebase Authentication</td>
            <td>Google LLC</td>
            <td>User sign-in (email, Google, Apple)</td>
            <td>EU / US (Google Cloud)</td>
          </tr>
          <tr>
            <td>Firebase Cloud Messaging</td>
            <td>Google LLC</td>
            <td>Push notifications</td>
            <td>EU / US (Google Cloud)</td>
          </tr>
          <tr>
            <td>Cloud Firestore</td>
            <td>Google LLC</td>
            <td>Server-side storage of workout and user data</td>
            <td>EU (europe-west1)</td>
          </tr>
          <tr>
            <td>Cloud Run</td>
            <td>Google LLC</td>
            <td>Hosts our backend API</td>
            <td>EU (europe-west1)</td>
          </tr>
          <tr>
            <td>PostHog</td>
            <td>PostHog Inc.</td>
            <td>Product analytics</td>
            <td>EU (eu.i.posthog.com)</td>
          </tr>
          <tr>
            <td>Sentry</td>
            <td>Functional Software Inc.</td>
            <td>Crash and error reporting</td>
            <td>EU / US</td>
          </tr>
          <tr>
            <td>YouTube (embedded)</td>
            <td>Google LLC</td>
            <td>Exercise demonstration videos</td>
            <td>Google infrastructure</td>
          </tr>
        </tbody>
      </table>
      <p>
        Where data is transferred to the US, it is covered by the EU–US Data
        Privacy Framework or Standard Contractual Clauses.
      </p>

      <h2>4. Embedded Content</h2>
      <p>
        The app embeds YouTube videos to demonstrate exercises. When you view
        an exercise with a video, your device connects to YouTube servers,
        which may set cookies and collect data according to{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google&apos;s Privacy Policy
        </a>
        .
      </p>

      <h2>5. Data Retention</h2>
      <ul>
        <li>
          <strong>Account data:</strong> retained until you delete your account.
        </li>
        <li>
          <strong>Guest device data:</strong> retained on our server until 12
          months of inactivity, then deleted.
        </li>
        <li>
          <strong>Analytics events:</strong> retained per PostHog&apos;s
          default retention policy.
        </li>
        <li>
          <strong>Crash reports:</strong> retained for 90 days in Sentry.
        </li>
        <li>
          <strong>Local device data:</strong> remains on your device until you
          uninstall the app or clear its data.
        </li>
      </ul>

      <h2>6. Your Rights</h2>
      <p>Under the GDPR you have the right to:</p>
      <ol>
        <li>
          <strong>Access</strong> the personal data we hold about you
          (Art.&nbsp;15).
        </li>
        <li>
          <strong>Rectification</strong> of inaccurate data (Art.&nbsp;16).
        </li>
        <li>
          <strong>Erasure</strong> (&quot;right to be forgotten&quot;) of your
          data (Art.&nbsp;17).
        </li>
        <li>
          <strong>Restrict</strong> processing (Art.&nbsp;18).
        </li>
        <li>
          <strong>Data portability</strong> — receive your data in a
          machine-readable format (Art.&nbsp;20).
        </li>
        <li>
          <strong>Object</strong> to processing based on legitimate interest
          (Art.&nbsp;21).
        </li>
        <li>
          <strong>Withdraw consent</strong> at any time for processing based on
          consent (Art.&nbsp;7(3)).
        </li>
        <li>
          <strong>Lodge a complaint</strong> with a supervisory authority. In
          Germany, this is typically the data protection authority of your
          federal state (<em>Landesdatenschutzbeauftragte</em>).
        </li>
      </ol>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:contact@beahead.ai">contact@beahead.ai</a>. We will
        respond within 30 days.
      </p>

      <h2>7. Cookies</h2>
      <p>
        The gymAhead mobile app does not use cookies. This website (
        <a href="https://gym.beahead.ai">gym.beahead.ai</a>) does not set any
        cookies or use any analytics or tracking scripts.
      </p>

      <h2>8. Children</h2>
      <p>
        gymAhead is not directed at children under the age of 16. We do not
        knowingly collect personal data from children. If you believe we have
        collected data from a child, please contact us and we will delete it
        promptly.
      </p>

      <h2>9. Health Disclaimer</h2>
      <p>
        gymAhead generates workout suggestions based on your inputs. It is{' '}
        <strong>not medical advice</strong>. Consult a qualified professional
        before starting any exercise program.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this policy from time to time. The &quot;Last updated&quot;
        date at the top of this page will reflect the most recent revision. If
        we make material changes, we will notify you within the app.
      </p>
    </LegalPageLayout>
  )
}
